import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useT } from '@/i18n';
import { Spinner, Wordmark } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { logError, logInfo } from '@/lib/log';

/** Paddle.js v2, loaded only on this page (the app's own bundle never carries it). */
const PADDLE_JS = 'https://cdn.paddle.com/paddle/v2/paddle.js';

interface PaddleGlobal {
  Environment: { set(env: 'sandbox' | 'production'): void };
  Initialize(o: { token: string; checkout?: { settings?: { successUrl?: string; displayMode?: 'overlay' | 'inline'; locale?: string } } }): void;
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`could not load ${src}`));
    document.head.appendChild(s);
  });
}

/**
 * `/pay/:provider?_ptxn=…`: Paddle's "default payment link" (plan §11.1; research note §2). Paddle sends the customer
 * here with the transaction id; Paddle.js opens its overlay checkout for it. Public: the customer may follow a
 * "re-enter payment details" link from an e-mail before signing in.
 */
export function PayPage() {
  const { provider = '' } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useT();
  const [error, setError] = useState(false);
  const ptxn = params.get('_ptxn');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await api.billing.checkoutConfig(provider, ptxn);
      if (!cfg.client_token) throw new Error('no client token configured');
      await loadScript(PADDLE_JS);
      if (cancelled || !window.Paddle) return;
      if (cfg.environment === 'sandbox') window.Paddle.Environment.set('sandbox');
      window.Paddle.Initialize({ token: cfg.client_token, checkout: { settings: { displayMode: 'overlay', locale, successUrl: new URL(cfg.success_url, window.location.origin).toString() } } });
      logInfo('billing', `paddle checkout opened provider=${provider} txn=${ptxn ?? '-'}`);
    })().catch((e) => {
      logError('billing', `pay page provider=${provider}`, e);
      if (!cancelled) setError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [provider, ptxn, locale]);
  return (
    <div className="page stack" style={{ maxWidth: 520, margin: '80px auto', gap: 18, alignItems: 'center', textAlign: 'center' }}>
      <Wordmark size={20} />
      {error ? <div className="alert alert-error" role="alert">{t('payUnavailable')}</div> : <Spinner label={t('payOpening')} />}
    </div>
  );
}
